import moment from 'moment';
import { getScorePercentage } from '@/domains/scoring';
import { getUserName } from './get-user-name';
import {
  ApiManagerTeamSummary,
  ApiMostRecentSessionSummary,
  IManagedUserSession,
  IManagerTeamSummary,
  IUser,
  TSimulationType,
} from '@/types';

/**
 * Maps API response data for a manager's team summary into the frontend-friendly format.
 *
 * This function transforms the nested structure of the API response into a more
 * convenient shape for frontend components, while also normalizing certain fields
 * and deriving additional properties like `certPass` for certification simulations.
 */
export function mapApiManagerDataToFrontend(
  apiManagerSummary: ApiManagerTeamSummary,
): IManagerTeamSummary {
  /**
   * Key mapping responsibilities:
   * 1. Convert API field names (e.g. `manager_id`) to frontend conventions (e.g. `managerId`).
   * 2. Map nested certifications and their managed users, applying similar transformations.
   * 3. For each managed user, map their most recent sessions (certificate, practice, roleplay) to the frontend format.
   * 4. Derive `certPass` for certification sessions based on the session's pass status and type.
   * 5. Apply defensive guards against null/undefined values in the API response to ensure stable frontend models.
   *    - For user IDs, if `user_id` is null, attempt to derive a fallback ID from the email prefix; if both are unavailable, use 'unknown_user'.
   *    - For user emails, if `user_email` is null, use 'unknown_email' as a fallback.
   * 6. Format `mostRecentActivity` using `moment` to display relative time (e.g. "a few seconds ago"), while handling nullability.
   * 7. Return the fully mapped `IManagerTeamSummary` object for use in the frontend, ensuring all nested structures are properly
   *    transformed and enriched with derived data where applicable.
   */
  const managersTeamSummary: IManagerTeamSummary = {
    // Map top-level manager summary fields.
    managerId: apiManagerSummary?.manager_id,
    managerName: apiManagerSummary?.manager_name,

    // Map each certification in the API summary to the frontend format,
    // including nested user summaries.
    certifications: apiManagerSummary?.products?.map((product) => ({
      // Map high-level certification fields.
      certificationId: product.product_id,
      certificationTitle: product.product_name,
      certificationTitleShort: product.product_name_short,

      // Map each managed user in the certification to the frontend format,
      // including their most recent sessions and activity.
      managedUsers: product.managed_users?.map((user) => {
        return {
          userId: user?.user_id || user?.user_email?.split('@')?.[0] || 'unknown_user',
          userName: getUserName({ name: user.user_name } as IUser, 'fullName'),
          userEmail: user.user_email || 'unknown_email',
          mostRecentCertificate: mapApiSession(user.most_recent_certificate),
          mostRecentPractice: mapApiSession(user.most_recent_practice),
          mostRecentRoleplay: mapApiSession(user.most_recent_roleplay),
          mostRecentActivity: user.most_recent_activity
            ? moment(user.most_recent_activity).fromNow()
            : null,
        };
      }),
    })),
  };

  // Return the fully mapped manager team summary for frontend consumption.
  return managersTeamSummary;
}

/**
 * Helper function to map an API session summary to the frontend IManagedUserSession format.
 * Returns null if the input session is null or undefined.
 *
 * @param session - The API session summary to map
 * @returns The mapped IManagedUserSession or null
 */
function mapApiSession(session: ApiMostRecentSessionSummary | null): IManagedUserSession | null {
  // Guard against null or undefined session input; return null in such cases to indicate no session data.
  if (!session || session == null) return null;

  // Map API session fields to frontend IManagedUserSession format, applying necessary transformations:
  // - Convert numeric IDs to strings for consistency in the frontend.
  // - Preserve nullability of optional fields like `submitted_at`.
  // - Cast `session_type` to TSimulationType, assuming API values align with frontend expectations.
  return {
    attemptCount: session.attempt_count,
    attemptNumber: session.attempt_number,
    certificationId: session.product_id,
    certificationTitle: session.product_name,
    certificationTitleShort: session.product_name_short,
    passed: session.passed,
    score: session.total_score,
    scoreMax: session.score_max,
    scorePercentage: getScorePercentage(session),
    sessionId: session.session_id,
    simulationId: session.scenario_id,
    simulationTitle: session.scenario_title,
    simulationTitleShort: session.scenario_title_short,
    simulationType: session.session_type as TSimulationType,
    submittedAt: session.submitted_at,
  };
}
